import React, { useState, useMemo, useEffect } from "react"
import { StyleSheet, View } from "react-native"
import { Text } from "../atoms"
import { Recurrence } from "../organisms/Recurrence"
import { buildRrule, getRecurrenceLabels, labelFromRrule, parseRrule, SimpleRecurrenceType, type CustomRecurrenceState } from "@utils"
import { ContextualDropdown } from './ContextualDropdown'
import { useLanguage } from '@context'

export const RecurrenceForm = ({
  value: rrule,
  onChange,
  dtstart,
}: {
  value: string | null | undefined
  onChange: (rrule: string | null) => void
  dtstart: Date
}) => {
  const { t } = useLanguage()
  const parsed = useMemo(() => parseRrule(rrule ?? null, dtstart), [rrule, dtstart])
  const [showRecurrence, setShowRecurrence] = useState(false)

  const recurrenceOptions = useMemo(() => {
    const labels = getRecurrenceLabels(t)
    return [
      { name: labels.never, id: 'never' },
      { name: labels.day, id: 'day' },
      { name: labels.week, id: 'week' },
      { name: labels['2weeks'], id: '2weeks' },
      { name: labels.month, id: 'month' },
      { name: labels['6weeks'], id: '6weeks' },
      { name: labels['3months'], id: '3months' },
      { name: labels['6months'], id: '6months' },
      { name: labels.year, id: 'year' },
      { name: labels.custom, id: 'custom' },
    ]
  }, [t])

  const displayLabel = useMemo(() => labelFromRrule(rrule ?? null, dtstart, t), [rrule, dtstart, t])
  const selectedId = recurrenceOptions.find(o => o.name === displayLabel)?.id

  useEffect(() => {
    if (parsed.custom && !showRecurrence) {
      setShowRecurrence(true)
    }
  }, [parsed])

  const handleSelect = (id: string) => {
    if (id === 'custom') {
      setShowRecurrence(true)
      return
    }
    setShowRecurrence(false)
    if (id === 'never') {
      onChange(null)
      return
    }
    const r = buildRrule({ type: id as SimpleRecurrenceType, dtstart })
    onChange(r)
  }

  const handleCustomValidate = (custom: CustomRecurrenceState) => {
    const r = buildRrule({ type: 'custom', dtstart, custom })
    onChange(r)
  }

  return (
    <>
      <View style={styles.container}>
        <Text type="bodySmall" style={styles.flex}>{t('recurrence.title')}</Text>
        <ContextualDropdown
          data={recurrenceOptions}
          label={showRecurrence ? getRecurrenceLabels(t).custom : displayLabel}
          selectedId={showRecurrence ? 'custom' : selectedId}
          onSelect={handleSelect}
        />
      </View>
      {showRecurrence && (
        <Recurrence
          initialCustom={parsed.custom}
          dtstart={dtstart}
          onValidate={handleCustomValidate}
        />
      )}
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  flex: {
    flex: 1,
  },
})
